import { useAuth } from '@shared/components/AuthProvider';
import { useFormik } from 'formik';
import { useState } from 'react';
import { isAuthProviderError } from '@apps/acquisitionFunnels/createWedding/utils';
import { isUserAlreadyExistsError } from '@shared/createEvent/utils/isAuthProviderError';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import { useCreateWeddingTelemetry } from '@apps/acquisitionFunnels/createWedding/CreateWedding.telemetry';
import { createAndAttachPixelImage } from '@apps/acquisitionFunnels/createWedding/components/PixelImage';
import { CREATE_WEDDING_APP_NAME } from '@apps/acquisitionFunnels/createWedding/constants';
import { useFieldErrors } from '@shared/hooks/useFieldErrors';
import * as Yup from 'yup';
import { useLocation, useTranslation } from '@shared/core';
import { useToast } from '@withjoy/joykit';
import { useCreateWeddingNavigation } from '@apps/acquisitionFunnels/createWedding/hooks/useCreateWeddingNavigation';
import type { CreateAccountFields } from '@apps/acquisitionFunnels/createWedding/CreateWedding.types';
import { recordCreateWeddingFlowEvent } from '@apps/acquisitionFunnels/createWedding/createWeddingFlowEvents';

export const useCreateAccountController = () => {
  const authProvider = useAuth();
  const [submissionError, setSubmissionError] = useState<string>('');
  const queryParams = useQueryParams();
  const telemetry = useCreateWeddingTelemetry();
  const { tq } = useTranslation('createWedding');
  const query = useLocation().search;
  const { navigateTo } = useCreateWeddingNavigation();
  const { toast } = useToast();

  const formik = useFormik<CreateAccountFields>({
    initialValues: { email: '', password: '' },
    validationSchema: Yup.object({
      email: Yup.string()
        .email(tq`Please enter a valid email address`)
        .required(tq`Please enter an email`),
      password: Yup.string().required(tq`Please enter a password`),
    }).required(),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async ({ email, password }) => {
      try {
        telemetry.createAccountClicked();
        await authProvider.loginManager.signupWithUsernameAndPassword(email, password, `/${CREATE_WEDDING_APP_NAME}${query ? `?${query}` : ''}`);
        recordCreateWeddingFlowEvent({ action: 'AccountCreated' });
      } catch (err) {
        telemetry.trackError('CreateAccount', err);
        recordCreateWeddingFlowEvent({
          action: 'AccountCreateFailed',
          errorMessage: isAuthProviderError(err) ? err.description : 'unknown',
        });
        if (isUserAlreadyExistsError(err)) {
          toast(tq`An account with this email already exists. Please sign in.`);
          navigateTo('signIn', { initialEmail: email });
        } else if (isAuthProviderError(err)) {
          setSubmissionError(err.description);
        }
      }
    },
  });

  const getFieldErrorProps = useFieldErrors<CreateAccountFields>(formik);

  const focusHandlers = {
    email: () => {
      createAndAttachPixelImage({ action: 'signup' });
      telemetry.inputHandler('createAccountEmail', 'onFocus');
    },
    password: () => telemetry.inputHandler('createAccountPassword', 'onFocus'),
  };

  return {
    formik,
    submissionError,
    focusHandlers,
    getFieldErrorProps,
    goToSignIn: () => navigateTo('signIn'),
    signInWithGoogle: () => authProvider.loginManager.loginWithGoogle(`/${CREATE_WEDDING_APP_NAME}`),
    signInWithFacebook: () => authProvider.loginManager.loginWithFacebook(`/${CREATE_WEDDING_APP_NAME}`),
    showSocialSignIn: ['google', 'facebook'].includes(`${queryParams.allowSignup}`),
  };
};
